import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { purchasesAPI } from '../api';
import DeliveryMap from '../components/DeliveryMap';
import { addressStreetLineOnly } from '../utils/addressUtils';

interface TrackingStage {
  key: string;
  label: string;
  completed: boolean;
  timestamp?: string | null;
}

interface DriverLocation {
  latitude: number | null;
  longitude: number | null;
  heading: number | null;
  updated_at: string | null;
}

interface TrackingInfo {
  id: number;
  status: string;
  active_stage: string;
  stages: TrackingStage[];
  title?: string;
  image_url?: string;
  price?: number;
  item_price?: number;
  shipping_fee?: number;
  sales_tax?: number;
  total_amount?: number;
  seller_name?: string;
  delivery_address?: string;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  delivery_photo?: string;
  estimated_arrival?: string;
  shipped_at?: string;
  created_at?: string;
  driver?: DriverLocation | null;
  city_name?: string;
  state_name?: string;
  delivery_time_option?: string;
  payment_method?: string | null;
}

const STAGE_ICONS: Record<string, string> = {
  order_placed: 'ri-shopping-bag-3-fill',
  order_accepted: 'ri-thumb-up-fill',
  preparing: 'ri-restaurant-fill',
  heading_to_you: 'ri-car-fill',
  driver_nearby: 'ri-map-pin-2-fill',
  delivered: 'ri-checkbox-circle-fill',
};

const STAGE_SUBLABELS: Record<string, string> = {
  order_placed: 'Waiting for seller to accept...',
  order_accepted: 'Seller has accepted your order',
  preparing: 'Your order is being prepared',
  heading_to_you: 'Your driver is on the way!',
  driver_nearby: 'Almost there!',
  delivered: 'Thank you!',
};

const STATUS_NOTIFICATION_MAP: Record<string, { title: string; body: (t?: string) => string }> = {
  processing: { title: 'Order Accepted!', body: (t) => `${t || 'Your order'} is being prepared.` },
  shipped: { title: 'Driver On The Way!', body: (t) => `Your driver is heading to you with ${t || 'your order'}.` },
  delivered: { title: 'Order Delivered!', body: (t) => `${t || 'Your order'} has been delivered. Thank you!` },
  cancelled: {
    title: 'Order Cancelled',
    body: (t) =>
      `The seller cancelled ${t ? `"${t}"` : 'your order'}. If you paid by card, the hold is released—you won’t be charged.`,
  },
};

function requestBrowserNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function sendBrowserNotification(status: string, itemTitle?: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const tmpl = STATUS_NOTIFICATION_MAP[status];
  if (!tmpl) return;
  try {
    new Notification(tmpl.title, {
      body: tmpl.body(itemTitle),
      icon: '/favicon-32.png',
    });
  } catch {
    // Notification API may fail in some contexts
  }
}

const TrackOrder: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  useAuth();
  const [tracking, setTracking] = useState<TrackingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevStatusRef = useRef<string | null>(null);

  // Request browser notification permission on mount
  useEffect(() => {
    requestBrowserNotificationPermission();
  }, []);

  const loadTracking = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await purchasesAPI.getTracking(parseInt(orderId));
      // Send browser notification if status changed
      if (prevStatusRef.current && prevStatusRef.current !== data.status) {
        sendBrowserNotification(data.status, data.title);
      }
      prevStatusRef.current = data.status;
      setTracking(data);
      setLoading(false);
    } catch (err: any) {
      if (!tracking) {
        setError(err.response?.data?.error || 'Failed to load order');
        setLoading(false);
      }
    }
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTracking();
    pollRef.current = setInterval(loadTracking, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadTracking]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    if (d.getTime() === today.getTime()) return 'Today';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatETA = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const end = new Date(date);
    end.setMinutes(end.getMinutes() + 30);
    const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${time} – ${endTime}`;
  };

  const formatPrice = (price: number | null | undefined) => {
    if (!price && price !== 0) return 'Free';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return 'Free';
    return `$${num.toFixed(2)}`;
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'pending': return 'Order placed! Waiting for the seller.';
      case 'processing': return 'Your order is being prepared!';
      case 'shipped': return 'Your order is on the way!';
      case 'delivered': return 'Your order has been delivered!';
      case 'cancelled': return 'This order has been cancelled.';
      default: return 'Tracking your order...';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-orange-500';
      case 'processing': return 'bg-blue-500';
      case 'shipped': return 'bg-purple-600';
      case 'delivered': return 'bg-green-600';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading order tracking...</p>
        </div>
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-red-400 mb-4 block"></i>
          <p className="text-xl text-red-600 mb-4">{error || 'Order not found'}</p>
          <Link to="/my-orders" className="text-green-600 hover:text-green-700 hover:underline">
            Go to My Orders
          </Link>
        </div>
      </div>
    );
  }

  const status = tracking.status?.toLowerCase() || 'pending';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">

        {/* Status Header Banner */}
        <div className={`${getStatusColor(status)} text-white rounded-t-2xl px-4 py-4 sm:px-6 sm:py-5`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm opacity-90">Order #{tracking.id}</p>
              <h1 className="text-lg sm:text-2xl font-bold mt-1 leading-snug">{getStatusMessage(status)}</h1>
              {tracking.estimated_arrival && status !== 'delivered' && status !== 'cancelled' && (
                <p className="text-xs sm:text-sm mt-2 opacity-90 break-words">
                  <i className="ri-time-line mr-1"></i>
                  Est. Arrival: {formatETA(tracking.estimated_arrival)}
                </p>
              )}
            </div>
            <div className="text-3xl sm:text-5xl opacity-30 flex-shrink-0 hidden sm:block" aria-hidden>
              {status === 'shipped' ? '🚗' : status === 'delivered' ? '✅' : status === 'processing' ? '👨‍🍳' : '📦'}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-b-2xl shadow-sm border border-gray-200 border-t-0">
          {/* Order Item Card */}
          <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-gray-100">
            <div className="flex items-start gap-3 sm:gap-4">
              {tracking.image_url ? (
                <img src={tracking.image_url} alt={tracking.title || 'Order'} className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-xl flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-xl sm:text-2xl">📦</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 break-words">{tracking.title || `Order #${tracking.id}`}</h3>
                    {tracking.seller_name && <p className="text-sm text-gray-500">from {tracking.seller_name}</p>}
                  </div>
                  <p className="text-base sm:text-lg font-bold text-gray-900 flex-shrink-0">
                    {formatPrice(tracking.total_amount ?? tracking.item_price ?? tracking.price)}
                  </p>
                </div>
              </div>
            </div>
            {/* Order breakdown: item + shipping + tax = total */}
            {(tracking.item_price != null || tracking.shipping_fee != null || tracking.sales_tax != null || tracking.total_amount != null) && (
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
                {tracking.item_price != null && (
                  <div className="flex justify-between text-gray-600">
                    <span>Items</span>
                    <span>{formatPrice(tracking.item_price)}</span>
                  </div>
                )}
                {(tracking.shipping_fee ?? 0) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{formatPrice(tracking.shipping_fee)}</span>
                  </div>
                )}
                {(tracking.sales_tax ?? 0) > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span>{formatPrice(tracking.sales_tax)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-100">
                  <span>Total</span>
                  <span>{formatPrice(tracking.total_amount ?? (Number(tracking.item_price ?? tracking.price ?? 0) + Number(tracking.shipping_fee ?? 0) + Number(tracking.sales_tax ?? 0)))}</span>
                </div>
              </div>
            )}
          </div>

          {/* Progress Steps - DoorDash style */}
          <div className="px-4 py-5 sm:px-6 sm:py-6">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-5">Order Progress</h2>

            {status === 'cancelled' ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                <div className="flex items-start space-x-3">
                  <i className="ri-store-2-line text-2xl text-red-500 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="font-semibold text-red-900">Cancelled by the seller</p>
                    <p className="text-sm text-red-800 mt-1">
                      The store cancelled this order. You won&apos;t be charged for items that weren&apos;t delivered.
                    </p>
                    {(tracking.payment_method || '').toLowerCase() === 'card' ? (
                      <p className="text-xs text-red-700/90 mt-2">
                        If you paid by card, your payment was only authorized (on hold) until delivery would complete. That hold is released as soon as this order is cancelled—you won&apos;t be charged.
                      </p>
                    ) : (
                      <p className="text-xs text-red-700/90 mt-2">
                        This order was cash on delivery — no payment was collected.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-0">
                {tracking.stages.map((stage, index) => {
                  const isActive = stage.key === tracking.active_stage;
                  const isLast = index === tracking.stages.length - 1;
                  const icon = STAGE_ICONS[stage.key] || 'ri-circle-fill';

                  return (
                    <div key={stage.key} className="flex">
                      {/* Left: circles + line */}
                      <div className="flex flex-col items-center mr-3 sm:mr-4">
                        {stage.completed ? (
                          <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                            <i className="ri-check-line text-white text-lg"></i>
                          </div>
                        ) : isActive ? (
                          <div className="relative flex-shrink-0">
                            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center shadow-sm">
                              <i className={`${icon} text-white text-sm`}></i>
                            </div>
                            <div className="absolute inset-0 w-9 h-9 rounded-full border-2 border-blue-300 animate-ping opacity-40"></div>
                          </div>
                        ) : (
                          <div className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center flex-shrink-0 bg-white">
                            <span className="text-xs font-semibold text-gray-400">{index + 1}</span>
                          </div>
                        )}
                        {!isLast && (
                          <div className={`w-0.5 h-10 ${stage.completed ? 'bg-green-400' : 'bg-gray-200'}`}></div>
                        )}
                      </div>
                      {/* Right: text */}
                      <div className="pt-1.5 pb-4 flex-1">
                        <p className={`text-sm font-semibold ${stage.completed ? 'text-gray-900' : isActive ? 'text-blue-600' : 'text-gray-400'}`}>
                          {stage.label}
                        </p>
                        {isActive && status !== 'delivered' && (
                          <p className="text-xs text-blue-500 mt-0.5">{STAGE_SUBLABELS[stage.key] || ''}</p>
                        )}
                        {stage.completed && stage.timestamp && (
                          <p className="text-xs text-gray-400 mt-0.5">{formatDate(stage.timestamp)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Driver Location Map — show as soon as the seller starts navigating (processing) and through shipped. */}
          {(status === 'shipped' || status === 'processing') && (tracking.driver?.latitude && tracking.driver?.longitude ? (
            <div className="px-4 pb-5 sm:px-6 sm:pb-6">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3">
                <i className="ri-car-fill text-blue-500 mr-2"></i>
                Driver Location
              </h2>
              <DeliveryMap
                deliveryAddress={tracking.delivery_address || ''}
                status={tracking.status}
                orderDate={tracking.created_at || new Date().toISOString()}
                deliveryTimeOption={tracking.delivery_time_option}
                driverLocation={{ lat: tracking.driver.latitude!, lng: tracking.driver.longitude! }}
                deliveryCoords={
                  tracking.delivery_latitude != null && tracking.delivery_longitude != null
                    ? { lat: tracking.delivery_latitude, lng: tracking.delivery_longitude }
                    : undefined
                }
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <i className="ri-refresh-line"></i>
                Updates every 5 seconds
              </p>
            </div>
          ) : (
            <div className="px-4 pb-5 sm:px-6 sm:pb-6">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4 flex items-center gap-3">
                <i className="ri-car-line text-2xl text-gray-400"></i>
                <div>
                  <p className="font-medium text-gray-700">Driver location</p>
                  <p className="text-sm text-gray-500">Live location will appear when the driver starts their trip</p>
                </div>
              </div>
            </div>
          ))}

          {/* Delivery Photo */}
          {status === 'delivered' && tracking.delivery_photo && (
            <div className="px-4 pb-5 sm:px-6 sm:pb-6">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3">
                <i className="ri-camera-fill text-green-500 mr-2"></i>
                Delivery Photo
              </h2>
              <img
                src={tracking.delivery_photo}
                alt="Delivery confirmation"
                className="w-full max-w-md mx-auto rounded-xl border border-gray-200 shadow-sm"
              />
            </div>
          )}

          {/* Delivery Address */}
          {tracking.delivery_address && (
            <div className="px-4 pb-5 sm:px-6 sm:pb-6">
              <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-2-fill text-xl text-red-500 mt-0.5 flex-shrink-0"></i>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Delivery Address</p>
                    <p className="text-sm text-gray-600 mt-0.5 break-words">
                      {addressStreetLineOnly(null, tracking.delivery_address)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-4 pb-4 sm:px-6 sm:pb-6 flex flex-col sm:flex-row gap-3">
            {tracking.seller_name && (
              <button
                onClick={() => navigate('/messages', {
                  state: {
                    sellerId: (tracking as any).seller_id,
                    sellerName: tracking.seller_name || 'Seller',
                  }
                })}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-xl transition text-sm w-full sm:w-auto"
              >
                <i className="ri-message-2-line"></i>
                <span>Contact Seller</span>
              </button>
            )}
            <Link
              to="/my-orders"
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-5 rounded-xl transition text-sm w-full sm:w-auto"
            >
              <i className="ri-arrow-left-line"></i>
              <span>All Orders</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
